import { ThunkType } from 'app/hooks/appHooks';
import { SearchMaskDirection } from 'app/models/types/store/location';
import {
  storeAutocompleteCache,
  storeAutocompleteResults,
  clearAutocompleteError,
  reportAutocompleteError,
  toggleAutocompleteLoading,
} from 'app/store/reducers/location';
import {
  autocompleteCacheSelector,
  departureCitySelector,
  optionsSelector,
} from 'app/store/selectors';
import { requestAutocompleteWithRetry } from 'app/store/thunk/location/requestAutocompleteWithRetryThunk';
import { isSpaceOnlyOrEmptyString } from 'app/functions';
import { AutocompleteResult } from 'app/models/types/store/location';

export type RequestAutocompleteType = {
  direction: SearchMaskDirection;
  query: string;
  sendDepartureCityId: boolean;
};

export const requestAutocomplete =
  ({ direction, query, sendDepartureCityId }: RequestAutocompleteType): ThunkType<void> =>
  async (dispatch, getState) => {
    const state = getState();
    if (isSpaceOnlyOrEmptyString(query)) {
      return;
    }
    const { locale }: ReturnType<typeof optionsSelector> = optionsSelector(state);

    const departureCity: ReturnType<typeof departureCitySelector> | null = sendDepartureCityId
      ? departureCitySelector(state)
      : null;

    const resultsKey = `${encodeURIComponent(query)}-${direction}${
      departureCity ? `-${departureCity.uuid}` : ''
    }-${locale}`;

    const autoCompleteCache: ReturnType<typeof autocompleteCacheSelector> =
      autocompleteCacheSelector(state);

    if (autoCompleteCache[resultsKey]) {
      dispatch(
        storeAutocompleteResults({
          results: autoCompleteCache[resultsKey],
          direction,
        }),
      );
      return;
    }

    dispatch(clearAutocompleteError({ direction }));
    dispatch(toggleAutocompleteLoading({ direction, loading: true }));

    try {
      // @ts-expect-error ignore
      const results: AutocompleteResult[] = await dispatch(
        requestAutocompleteWithRetry({
          query,
          departureCity: (departureCity && departureCity.uuid) || null,
          direction,
        }),
      );

      dispatch(storeAutocompleteResults({ results, direction }));
      dispatch(storeAutocompleteCache({ results, key: resultsKey }));
    } catch (err) {
      dispatch(
        reportAutocompleteError({
          id: 'error.autocomplete',
          level: 'Failed to fetch results.',
          direction,
        }),
      );
    }

    dispatch(toggleAutocompleteLoading({ direction, loading: false }));
  };
